import React from 'react';
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Flex,
  Grid,
  GridItem,
  Card,
  CardHeader,
  CardBody,
  Icon,
  UnorderedList,
  ListItem,
  useColorMode,
} from '@chakra-ui/react';
import { 
  AtSignIcon, 
  StarIcon, 
  TimeIcon, 
  CheckIcon 
} from '@chakra-ui/icons';
import Head from 'next/head';



const AptofyCaseStudy = () => {

    const { colorMode } = useColorMode();
const bgColor = colorMode === "dark" ? "gray.900" : "gray.50";
const textColor = colorMode === "dark" ? "white" : "gray.700";

  const challengeIcons = [
    AtSignIcon,
    StarIcon,
    TimeIcon,
    CheckIcon
  ];

  const challenges = [
    "Global Talent Acquisition",
    "Bias and Inclusivity",
    "Operational Inefficiencies",
    "Team Fit and Communication"
  ];

  const resultHighlights = [
    {
      title: "Scalability and Speed",
      description: "50% reduction in hiring timelines and seamless global recruitment"
    },
    {
      title: "Team Diversity",
      description: "Bias-free, objective assessments fostering inclusive hiring"
    },
    {
      title: "Sustainability",
      description: "Virtual processes reducing carbon footprint and recruitment costs"
    }
  ];

  return (
    <Box bg={bgColor} minHeight="100vh" py={12}>
      <Head>
        <title>Aptofy Case Study: Revolutionizing Global Hiring</title>
        <meta name="description" content="Discover how Aptofy transforms recruitment with AI-powered, bias-free hiring solutions" />
      </Head>
      
      <Container maxW="container.xl">
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <Box textAlign="center" mb={10}>
            <Heading 
              as="h1" 
              size="2xl" 
              color="blue.600" 
              mb={4}
            >
               Building High Peformance Teams the smart and sustainable way
            </Heading>
            {/* <Text fontSize="xl" color="gray.600">
              AI-Powered, Bias-Free, and Sustainable Recruitment Solutions
            </Text> */}
          </Box>

          {/* Background */}
          <Card bg={bgColor} boxShadow="md" p={6}>
            <CardHeader>
              <Heading size="lg" color="blue.500">Background</Heading>
            </CardHeader>
            <CardBody>
              <Text>
                Building diverse, inclusive, and high-performing teams is essential for organizations navigating new technologies, products, and markets. Aptofy is an AI-powered platform designed to overcome traditional recruitment challenges by combining customizable, expert-designed assessments with 100% proctored video interviews.
              </Text>
            </CardBody>
          </Card>

          {/* Challenges */}
          <Box>
            <Heading size="lg" mb={6} textAlign="center" color="blue.500">
              Key Challenges
            </Heading>
            <Grid templateColumns={['1fr', 'repeat(2, 1fr)', 'repeat(4, 1fr)']} gap={6}>
              {challenges.map((challenge, index) => (
                <GridItem key={challenge}>
                  <Card bg={bgColor} boxShadow="md" h="full">
                    <CardBody display="flex" alignItems="center">
                      <Icon 
                        as={challengeIcons[index]} 
                        w={10} 
                        h={10} 
                        color="blue.400" 
                        mr={4}
                      />
                      <Text fontWeight="bold">{challenge}</Text>
                    </CardBody>
                  </Card>
                </GridItem>
              ))}
            </Grid>
          </Box>

          {/* Solution */}
          <Card bg={bgColor} boxShadow="md">
            <CardHeader>
              <Heading size="lg" color="blue.500">Solution</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Text fontWeight="bold">Key Solution Components:</Text>
                <UnorderedList pl={4}>
                  <ListItem>Customizable, Expert-Led Assessments</ListItem>
                  <ListItem>100% Proctored Video Interviews</ListItem>
                  <ListItem>Bias-Free and Inclusive Hiring Approach</ListItem>
                  <ListItem>Sustainable Recruitment Methodology</ListItem>
                </UnorderedList>
              </VStack>
            </CardBody>
          </Card>

          {/* Results */}
          <Box>
            <Heading size="lg" mb={6} textAlign="center" color="blue.500">
              Transformative Results
            </Heading>
            <Grid templateColumns={['1fr', 'repeat(3, 1fr)']} gap={6}>
              {resultHighlights.map((result) => (
                <GridItem key={result.title}>
                  <Card bg={bgColor} boxShadow="md" h="full">
                    <CardBody>
                      <Heading size="md" mb={3} color="blue.600">
                        {result.title}
                      </Heading>
                      <Text color="gray.600">{result.description}</Text>
                    </CardBody>
                  </Card>
                </GridItem>
              ))}
            </Grid>
          </Box>

          {/* Conclusion */}
          <Card bg={bgColor} boxShadow="md">
            <CardBody>
              <Text fontStyle="italic" textAlign="center">
                "Aptofy is redefining recruitment with innovative, proctored video interviews and expert-driven, customizable assessments."
              </Text>
            </CardBody>
          </Card>
        </VStack>
      </Container>
    </Box>
  );
};

export default AptofyCaseStudy;